import { type FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form } from 'react-bootstrap';

import { type GeoMapElement as ManifestGeoMapElement } from '@soomo/lib/types/WebtextManifest';
import { GeoMapFigure } from '@soomo/lib/components/pageElements/index';
import themes from '@soomo/lib/styles/themes/index';

import { type GeoMap, GeoMapScaleType, GeoMapType } from '@editor2/types/pageElements';
import { geoMapToGeoMapProperties } from '@editor2/mappers/element';
import { updateElement } from '@editor2/reducers/editor/editorSlice';
import {
	getDefaultScaleByMapType,
	getGeoMapInitData,
	getGeoMapInitLabels,
	getGeoMapTooltipTemplate,
	mergeGeoMapData
} from '@editor2/services/geoMapService';

import CopyInput from '@editor2/components/CopyInputWrapper';
import CodeMirrorHTML from '@editor2/components/CodeMirrorHTML';
import GroupsTable from './GroupsTable';
import DataTableWithPagination from './DataTableWithPagination';

import { elementCommonStyles } from '@editor2/styles/element';
import { geoMapElement } from './styles';

import { MAP_HEIGHT, MAP_WIDTH } from './constants';
import { showToast } from '@editor2/reducers/toastSlice';
import { ToastSeverity } from '@editor2/types/toastSeverity';
import { type RootState } from '@editor2/reducers/root';
import { useDebouncedDispatch } from '@editor2/hooks/useDebouncedDispatch';
import { useDebounceProtectedState } from '@editor2/hooks/useDebounceProtectedState';

interface Props {
	element: GeoMap;
	courseId: number;
}

const GeoMapElement: FC<Props> = (props) => {
	const { element, courseId } = props;

	const commonPayload = { elementId: element.family_id, courseId };

	const dispatch = useDispatch();
	const debouncedDispatch = useDebouncedDispatch();

	const { theme: themeName } = useSelector((state: RootState) => state.courses[courseId]);

	const [geoMapProperties, handleLocalChange] = useDebounceProtectedState(
		element,
		geoMapToGeoMapProperties
	);

	const marginsOrder = ['top', 'right', 'bottom', 'left'];

	const viewBoxWidth = MAP_WIDTH - +geoMapProperties.margins[1] - +geoMapProperties.margins[3];
	const viewBoxHeight = MAP_HEIGHT - +geoMapProperties.margins[0] - +geoMapProperties.margins[2];
	const defaultTranslate = [viewBoxWidth / 2, viewBoxHeight / 2];

	const handleChange = (value) => {
		handleLocalChange(value);
		debouncedDispatch(updateElement({ ...commonPayload, ...value }));
	};

	const isDataTableModified = () => {
		const newColumns = element.labels.length > 1;
		const groupColumnFilled = Object.keys(element.data).some((key) => !!element.data[key][0]);

		return newColumns || groupColumnFilled;
	};

	const onGeoMapTypeChange = (type: GeoMapType) => {
		if (
			!isDataTableModified() ||
			(isDataTableModified() &&
				// eslint-disable-next-line no-alert
				window.confirm(
					`Are you sure to change the map type? All data in the table will be erased.`
				))
		) {
			const newData = getGeoMapInitData(type);
			const labels = getGeoMapInitLabels();
			const tooltip = getGeoMapTooltipTemplate(type);

			handleChange({ geomap_type: type, data: newData, labels, tooltip });
		}
	};

	const updateMapWithJSONData = (geoMapPayload: Partial<GeoMap>) => {
		const { geomap_type, data, labels, colors } = geoMapPayload;

		if (geomap_type !== element.geomap_type) {
			dispatch(
				showToast({
					severity: ToastSeverity.Error,
					message: 'Incorrect map type of imported GeoMap'
				})
			);
			throw new Error('Incorrect map type of imported GeoMap');
		}

		const mergedData = mergeGeoMapData(element.data, data, labels);
		handleChange({ labels, colors, data: mergedData });
	};

	const onImportFromJSONClick = (event) => {
		const file = event.target.files[0];

		const fileReader = new FileReader();
		fileReader.readAsText(file);
		fileReader.onload = (e) => {
			const geoMapPayload = JSON.parse(e.target.result as string);
			updateMapWithJSONData(geoMapPayload);
		};
	};

	return (
		<>
			<Form className={`${elementCommonStyles} ${geoMapElement}`}>
				<CopyInput label="Family ID" value={element.family_id} />

				<Form.Group>
					<Form.Label>Number</Form.Label>
					<Form.Control
						value={geoMapProperties.figure_number}
						className="number"
						placeholder="Figure number"
						onChange={(e) => handleChange({ figure_number: e.target.value })}
					/>
				</Form.Group>

				<Form.Group>
					<Form.Label>Title</Form.Label>
					<Form.Control
						value={geoMapProperties.title}
						className="title"
						placeholder="Figure title"
						onChange={(e) => handleChange({ title: e.target.value })}
					/>
				</Form.Group>

				<Form.Group>
					<Form.Label>Instructions</Form.Label>
					<Form.Control
						as="textarea"
						rows={3}
						value={geoMapProperties.instructions}
						placeholder="Figure instructions"
						onChange={(e) => handleChange({ instructions: e.target.value })}
					/>
				</Form.Group>

				<Form.Group
					className="mapPreview"
					style={{ maxWidth: themes[themeName]?.layout.contentColumnWidth || '700px' }}>
					<Form.Label>Preview</Form.Label>
					<GeoMapFigure figure={{ ...element, version: 0 } as unknown as ManifestGeoMapElement} />
				</Form.Group>

				<Form.Group>
					<Form.Label>Map Type</Form.Label>
					<Form.Control
						as="select"
						className="select"
						value={geoMapProperties.geomap_type}
						onChange={(e) => onGeoMapTypeChange(e.target.value as GeoMapType)}>
						{Object.keys(GeoMapType).map((type) => (
							<option key={type} value={GeoMapType[type]}>
								{type}
							</option>
						))}
					</Form.Control>
				</Form.Group>

				<div className="initialValuesBlock">
					<Form.Group>
						<Form.Label>Initial Scale</Form.Label>
						<div className="initialScaleBlock">
							<Form.Control
								as="select"
								className="select"
								value={geoMapProperties.scale?.type}
								onChange={(e) =>
									handleChange({
										scale: {
											type: e.target.value,
											value: getDefaultScaleByMapType(geoMapProperties.geomap_type)
										}
									})
								}>
								{Object.keys(GeoMapScaleType).map((type) => (
									<option key={type} value={GeoMapScaleType[type]}>
										{type}
									</option>
								))}
							</Form.Control>

							{geoMapProperties.scale?.type === GeoMapScaleType.custom && (
								<Form.Control
									type="number"
									className="number"
									step={1}
									value={geoMapProperties.scale?.value}
									onChange={(e) =>
										handleChange({
											scale: { ...geoMapProperties.scale, value: e.target.value }
										})
									}
								/>
							)}
						</div>
					</Form.Group>

					<Form.Group>
						<Form.Label>Initial Translate</Form.Label>
						<div className="initialScaleBlock">
							<Form.Control
								as="select"
								className="select"
								value={geoMapProperties.translate?.type}
								onChange={(e) =>
									handleChange({
										translate: { type: e.target.value, value: defaultTranslate }
									})
								}>
								{Object.keys(GeoMapScaleType).map((type) => (
									<option key={type} value={GeoMapScaleType[type]}>
										{type}
									</option>
								))}
							</Form.Control>

							{geoMapProperties.translate?.type === GeoMapScaleType.custom && (
								<>
									<Form.Group>
										<Form.Control
											type="number"
											className="number"
											step={1}
											value={geoMapProperties.translate?.value[0]}
											onChange={(e) =>
												handleChange({
													translate: {
														...geoMapProperties.translate,
														value: [e.target.value, geoMapProperties.translate?.value[1]]
													}
												})
											}
										/>
										<Form.Text className="text-muted">width</Form.Text>
									</Form.Group>

									<Form.Group>
										<Form.Control
											type="number"
											className="number"
											step={1}
											value={geoMapProperties.translate?.value[1]}
											onChange={(e) =>
												handleChange({
													translate: {
														...geoMapProperties.translate,
														value: [geoMapProperties.translate?.value[0], e.target.value]
													}
												})
											}
										/>
										<Form.Text className="text-muted">height</Form.Text>
									</Form.Group>
								</>
							)}
						</div>
					</Form.Group>
				</div>

				<Form.Group>
					<Form.Label>Load data from GeoMap JSON file</Form.Label>
					<Form.Control type="file" onChange={(e) => onImportFromJSONClick(e)} />
				</Form.Group>

				<Form.Group>
					<Form.Label>Map Groups</Form.Label>
					<GroupsTable element={element} courseId={courseId} />
				</Form.Group>

				<Form.Group>
					<Form.Label>Map Data</Form.Label>
					<DataTableWithPagination element={element} courseId={courseId} />
				</Form.Group>

				<Form.Group>
					<Form.Label>Tooltip template</Form.Label>
					<CodeMirrorHTML
						value={geoMapProperties.tooltip}
						showTips={false}
						onChange={(value) => handleChange({ tooltip: value })}
					/>
					<Form.Text className="text-muted">
						(Put column name in square brackets to be replaced with the corresponding value)
					</Form.Text>
				</Form.Group>

				<Form.Group>
					<Form.Label>Margin adjustments (in pixels)</Form.Label>

					<div className="marginsBlock">
						{geoMapProperties.margins &&
							geoMapProperties.margins.map((margin, i) => (
								<Form.Group key={i}>
									<Form.Control
										type="number"
										step={1}
										value={margin || 0}
										className="number"
										onChange={(e) =>
											handleChange({
												margins: [
													...geoMapProperties.margins.slice(0, i),
													e.target.value,
													...geoMapProperties.margins.slice(i + 1)
												]
											})
										}
									/>
									<Form.Text className="text-muted">{marginsOrder[i]}</Form.Text>
								</Form.Group>
							))}
					</div>
				</Form.Group>

				<Form.Group>
					<Form.Label>Description</Form.Label>
					<CodeMirrorHTML
						value={geoMapProperties.description}
						showTips={false}
						options={{ placeholder: 'Description (for accessibility)' }}
						onChange={(value) => handleChange({ description: value })}
					/>
				</Form.Group>

				<Form.Group>
					<Form.Label>Caption</Form.Label>
					<CodeMirrorHTML
						value={geoMapProperties.caption}
						showTips={false}
						options={{ placeholder: 'Figure caption' }}
						onChange={(value) => handleChange({ caption: value })}
					/>
				</Form.Group>

				<Form.Group>
					<Form.Label>Credit(s)</Form.Label>
					<CodeMirrorHTML
						value={geoMapProperties.credits}
						showTips={false}
						options={{ placeholder: 'Figure credits' }}
						onChange={(value) => handleChange({ credits: value })}
					/>
				</Form.Group>
			</Form>
		</>
	);
};

export default GeoMapElement;
